import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import React = require('react');
import _ = require('underscore');

import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ExplorerTestIds } from '~src/spacesPlatform/components/Explorer/Explorer';
import { ExplorerEmptyState } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { Space } from '~src/spacesPlatform/components/spaceComponents/Space';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { FilterMap } from '~src/spacesPlatform/state/ContentExplorerState';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { Formatters, Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { interpolateString } from '../../common/utilities/StringInterpolation';
import { SearchSpaceStyles } from './SearchSpaceStyles';

export interface UnifiedSearchSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly searchTerm: string;
  readonly contentFilters: FilterMap;
  readonly isFetchingContent: boolean;
  readonly fetchedContentCount: number;
}

export interface UnifiedSearchSpaceDispatchProps {

}

export interface UnifiedSearchSpaceOwnProps {
}

export type SearchSpaceProps = UnifiedSearchSpaceStateProps & UnifiedSearchSpaceDispatchProps & UnifiedSearchSpaceOwnProps;

export interface SearchSpaceState {

}

export class UnifiedSearchSpace extends React.PureComponent<SearchSpaceProps, SearchSpaceState> {
  constructor(props: SearchSpaceProps) {
    super(props);
  }

  public render(): JSX.Element {
    // we don't check the contentCountUpToDate prop becasue we don't want the tabs to flicker in as we
    // search and have no results, but we want them to show the first time we render (TFS922001)
    const emptySearch = this.props.fetchedContentCount === 0 && !this.hasExtraFilters();
    if (!emptySearch) {
      return (
        <div className={ SpaceContainerStyles.SpaceContainer }>
          <div className={ SpaceContainerStyles.MastheadArea }>
            { this.renderMasthead() }
          </div>
          <div className={SpaceContainerStyles.SubplaceArea}>
            <div className={SpaceContainerStyles.SubplaceContainer}>
              <ConnectedExplorer
                supportedDisplayModes={['list', 'thumbnail']}
                hideSelectionAndActionMenu={false}
                toolbarProps= {{
                  supportFilterPanel: false,
                  hideSelection: false,
                  toolbarVisibility: 'enabled'
                }}
                emptyStateProps={{
                  primaryMessage: Messages.searchSpaceNoResultsPrimary()
                }}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return this.renderEmptySearchSpace();
    }
  }

  private renderEmptySearchSpace() {
    const primaryMessage = this.props.searchTerm
      ? Messages.searchSpaceNoResultsPrimary()
      : Messages.searchSpaceNoSearchStringPrimary();
    const secondaryMessage = this.props.searchTerm
      ? Messages.searchSpaceNoResultsSecondary()
      : Messages.searchSpaceNoSearchStringSecondary();
    const renderContent = () => {
      return (
        <span>
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContent}
            iconUrl={imgPath('spaces_empty_search.svg')}
            primaryMessage={primaryMessage}
            secondaryMessage={secondaryMessage}
          />
        </span>
      );
    };
    return <Space renderMasthead={this.renderMasthead} renderContent={renderContent}/>;
  }

  private hasExtraFilters(): boolean {
    // With no additional filters applied, there should only be 1 filter in the app store (the 'search' filter)
    return Object.keys(this.props.contentFilters).length > 1;
  }

  private renderMasthead = () => {
    return(
      <div>
        <div style={{display: 'flex', flexDirection: 'column'}}>
          <span className={SearchSpaceStyles.Results}>
            {Messages.searchSpaceResults()}
          </span>
          <span data-tb-test-id="search-detail-results" className={SearchSpaceStyles.ResultsDetails}>
            {this.resultsDetails()}
          </span>
        </div>
      </div>
    );
  }

  private resultsDetails = () => {
    if (this.props.searchTerm.length === 0) {
      return '';
    } else if (this.props.isFetchingContent) {
      return interpolateString(Messages.searchSpaceResultsDetailsSpinner(), this.replacements());
    } else {
      const total = this.props.fetchedContentCount;
      if (total === 0) {
        return interpolateString(Messages.searchSpaceResultsDetailsNone(), this.replacements());
      } else if (total === 1) {
        return interpolateString(Messages.searchSpaceResultsDetailsOne(), this.replacements(total));
      } else {
        return interpolateString(Messages.searchSpaceResultsDetailsMultiple(), this.replacements(total));
      }
    }
  }

  private replacements = (totalResults: number = 0) => {
    return {
      ITEM_COUNT:  <span key={1} className={SearchSpaceStyles.ResultsDetailsBold}>{Formatters.formatCount(totalResults)}</span>,
      SEARCH_TERM: <span key={2} className={SearchSpaceStyles.ResultsDetailsBold}>{this.props.searchTerm}</span>,
      SPINNER: <span key={1}>
                 <ActivitySpinnerWidget testId={'search-space-waiting'} spinnerSize={ActivitySpinnerSize.Small} />
               </span>
    };
  }
}
